import React from "react";
import { useTranslation } from "react-i18next";
import { Trash2, Plus, CreditCard } from "lucide-react";
import { useCheckoutStore } from "../store/checkout-store";
import { PaymentBrandLogo } from "@/features/settings/components/PaymentBrandLogo";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/common/loading-spinner";
import { useGetSavedCards } from "@/api/hooks/use-query-api";
import type { SavedCard } from "@/api/models/api-types";
import { CardFields } from "@/features/cards/components/CardFields";
import { useCardForm } from "@/features/cards/hooks/use-card-form";

export const PaymentMethodSection: React.FC = () => {
  const { t } = useTranslation();
  const {
    cardMode,
    selectedSavedCardToken,
    setSelectedSavedCard,
    setActiveBinNumber,
    setNewCardData,
    openDeletePaymentMethodDialog,
  } = useCheckoutStore();

  // Backend'den saklı kartları çek
  const { data: savedCardsData, isLoading: isSavedCardsLoading } =
    useGetSavedCards();
  const savedCards: SavedCard[] = savedCardsData?.data?.cards ?? [];
  const hasSavedCards = savedCards.length > 0;

  // Yeni kart formu — "Kayıtlı Kartlarım" ile aynı form, `pay` modunda:
  // CVV ve "kartı kaydet" anahtarı eklenir, geri kalanı birebir aynıdır.
  const [showNewCardForm, setShowNewCardForm] = React.useState(!hasSavedCards);
  const form = useCardForm("pay");
  const { values, isValid, toPaymentCardPayload } = form;

  // Saklı kartlar yüklenince, kart yoksa formu göster; varsa listeyi göster
  React.useEffect(() => {
    if (!isSavedCardsLoading) {
      setShowNewCardForm(savedCards.length === 0);
      // İlk saklı kartı varsayılan seç
      if (savedCards.length > 0 && !selectedSavedCardToken) {
        const first = savedCards[0];
        setSelectedSavedCard(first.cardToken, first.binNumber);
      }
    }
  }, [isSavedCardsLoading, savedCards.length]);

  // Kart numarası değişince BIN'i güncelle — sadece yeni kart formu açıkken
  // showNewCardForm false iken saklı kartın BIN'ini silmemek için guard eklendi
  React.useEffect(() => {
    if (!showNewCardForm) return;
    const digits = values.cardNumber.replace(/\D/g, "");
    if (digits.length >= 8) {
      setActiveBinNumber(digits.slice(0, 8));
    } else {
      setActiveBinNumber(null);
    }
  }, [values.cardNumber, showNewCardForm, setActiveBinNumber]);

  // Form geçerli olduğu anda checkout store'a sync et — sadece yeni kart formu
  // açıkken; kapalıyken `setNewCardData(null)` çağrılıp cardMode: "saved"
  // ezilmesin. Payload'ı hook üretir: yıl formatı ve alias kuralı kart ekleme
  // formuyla aynı yerden gelir.
  React.useEffect(() => {
    if (!showNewCardForm) return;
    if (isValid) {
      const payload = toPaymentCardPayload();
      setNewCardData({
        cardHolderName: payload.cardHolderName,
        cardNumber: payload.cardNumber,
        expireMonth: payload.expireMonth,
        expireYear: payload.expireYear,
        cvc: payload.cvc,
        saveCard: values.saveCard,
        alias: values.cardAlias.trim(),
      });
    } else {
      setNewCardData(null);
    }
  }, [showNewCardForm, isValid, values.saveCard, toPaymentCardPayload, setNewCardData]);

  const handleSelectSavedCard = (card: SavedCard) => {
    setSelectedSavedCard(card.cardToken, card.binNumber);
    setShowNewCardForm(false);
  };

  const handleDeleteSavedCard = (e: React.MouseEvent, card: SavedCard) => {
    e.stopPropagation();
    // deletingPaymentMethodId olarak cardToken kullan
    openDeletePaymentMethodDialog(card.cardToken);
  };

  const handleCancelNewCard = () => {
    setShowNewCardForm(false);
    // İlk saklı kartı yeniden seç
    if (savedCards.length > 0) {
      setSelectedSavedCard(savedCards[0].cardToken, savedCards[0].binNumber);
    }
    form.reset();
  };

  const getCardBrand = (card: SavedCard) => {
    return card.cardBrand || card.cardAssociation || card.cardType || "Card";
  };

  // Satır başlığı: alias önce — kullanıcının kendi verdiği ad, markadan daha
  // ayırt edici. "Kayıtlı Kartlarım" ve widget da aynı sırayı kullanır.
  const getCardBrandLabel = (card: SavedCard) => {
    return card.cardAlias || getCardBrand(card);
  };

  return (
    <div className="border border-[#e3e8ef] dark:border-gray-700 rounded-[12px] overflow-hidden">
      {/* Section Header */}
      <div className="bg-[#fcfcfd] dark:bg-gray-800 border-b border-[#e3e8ef] dark:border-gray-700 flex items-center justify-between px-3 md:px-4 py-2.5 md:py-3">
        <h2 className="text-base md:text-lg lg:text-[20px] font-semibold leading-6 md:leading-7 lg:leading-[30px] text-[#121926] dark:text-gray-100">
          {t("checkout.payment")}
        </h2>
        {hasSavedCards && !showNewCardForm && (
          <button
            onClick={() => {
              setShowNewCardForm(true);
              // Yeni kart formuna geçince saklı kartın BIN'ini temizle ve yeni kart moduna geç
              setActiveBinNumber(null);
              setNewCardData(null);
            }}
            className="text-xs md:text-sm font-semibold text-[#6172f3] hover:text-[#4c5fd5] transition-colors flex items-center gap-1"
          >
            <Plus className="w-3.5 h-3.5" />
            {t("checkout.addNewPayment")}
          </button>
        )}
      </div>

      <div className="p-3 md:p-4 space-y-3">
        {isSavedCardsLoading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="sm" />
          </div>
        ) : (
          <>
            {/* Saklı Kartlar Listesi */}
            {hasSavedCards && !showNewCardForm && (
              <div className="space-y-2">
                {savedCards.map((card) => {
                  const isSelected = selectedSavedCardToken === card.cardToken && cardMode === "saved";
                  return (
                    <div
                      key={card.cardToken}
                      onClick={() => handleSelectSavedCard(card)}
                      className={`flex items-center justify-between gap-3 p-3 rounded-[12px] border transition-all cursor-pointer ${
                        isSelected
                          ? "bg-[#f5f8ff] dark:bg-blue-950/30 border-[#6172f3]"
                          : "bg-transparent border-[#e3e8ef] dark:border-gray-700 hover:border-[#6172f3]/50"
                      }`}
                    >
                      <div className="flex items-center gap-3 flex-1">
                        {/* Radio */}
                        <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                          isSelected ? "border-[#6172f3] bg-white" : "border-[#e3e8ef] dark:border-gray-600 bg-white dark:bg-gray-700"
                        }`}>
                          {isSelected && <div className="w-2 h-2 rounded-full bg-[#6172f3]" />}
                        </div>

                        {/* Kart Logo */}
                        <div className="w-[52px] h-[36px] flex items-center justify-center flex-shrink-0">
                          <PaymentBrandLogo brand={getCardBrand(card).toLowerCase()} className="w-full h-full" />
                        </div>

                        {/* Kart Detayları */}
                        <div className="flex flex-col gap-0.5 min-w-0">
                          <p className="text-xs md:text-sm font-semibold text-[#121926] dark:text-gray-100">
                            {getCardBrandLabel(card)} •••• {card.lastFourDigits}
                          </p>
                          <p className="text-[10px] md:text-xs text-[#697586] dark:text-gray-400 flex items-center gap-1">
                            {card.cardBankName}
                            {card.cardExpiryStatus === "EXPIRED" && (
                              <span className="text-red-500">{t("checkout.cardExpired")}</span>
                            )}
                          </p>
                        </div>
                      </div>

                      {/* Sil Butonu */}
                      <button
                        onClick={(e) => handleDeleteSavedCard(e, card)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors flex-shrink-0"
                        title={t("common.delete")}
                      >
                        <Trash2 className="w-4 h-4 text-red-600 dark:text-red-400" />
                      </button>
                    </div>
                  );
                })}

                {/* Yeni kart ile ödeme seçeneği */}
                <div
                  onClick={() => {
                    setShowNewCardForm(true);
                    setSelectedSavedCard(null, null);
                  }}
                  className={`flex items-center gap-3 p-3 rounded-[12px] border transition-all cursor-pointer ${
                    cardMode === "new"
                      ? "bg-[#f5f8ff] dark:bg-blue-950/30 border-[#6172f3]"
                      : "bg-transparent border-[#e3e8ef] dark:border-gray-700 hover:border-[#6172f3]/50"
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    cardMode === "new" ? "border-[#6172f3] bg-white" : "border-[#e3e8ef] dark:border-gray-600 bg-white dark:bg-gray-700"
                  }`}>
                    {cardMode === "new" && <div className="w-2 h-2 rounded-full bg-[#6172f3]" />}
                  </div>
                  <CreditCard className="w-5 h-5 text-[#697586] dark:text-gray-400" />
                  <span className="text-xs md:text-sm font-medium text-[#364152] dark:text-gray-300">
                    {t("checkout.payWithNewCard")}
                  </span>
                </div>
              </div>
            )}

            {/* Yeni Kart Formu */}
            {(showNewCardForm || !hasSavedCards) && (
              <div className="space-y-4">
                <CardFields form={form} idPrefix="checkout" />

                {/* Saklı kartlar varsa iptal butonu */}
                {hasSavedCards && (
                  <Button
                    variant="outline"
                    onClick={handleCancelNewCard}
                    className="w-full border-[#e3e8ef] dark:border-gray-700 text-[#414651] dark:text-gray-300"
                  >
                    {t("common.cancel")}
                  </Button>
                )}
              </div>
            )}
          </>
        )}

        {/* Güvenli Ödeme */}
        <div className="flex items-center gap-1.5 mt-3 md:mt-4">
          <span className="text-[#697586]">🔒</span>
          <p className="text-[10px] md:text-xs text-[#697586] dark:text-gray-400">
            {t("checkout.securePayment")}
          </p>
        </div>
      </div>
    </div>
  );
};
